// P5.1 (2026-11-03) — Org Settings page.
//
// Owner-facing. One place to set the IANA timezone used across every
// publish surface (calendar, queue, receipts, fan-out, week scheduler,
// adapter explorer, digest). Default America/New_York is seeded via
// migration; owners can change it here.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { refreshOrgTimezone, useOrgTimezone } from "../lib/tz";

const COMMON_ZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Toronto",
  "America/Vancouver",
  "America/Mexico_City",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Istanbul",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Hong_Kong",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Australia/Sydney",
  "Pacific/Auckland",
  "UTC",
];

function dedupe(arr) { return Array.from(new Set(arr)); }

export default function Settings() {
  const tz = useOrgTimezone();
  const [settings, setSettings] = useState(null);
  const [picked, setPicked] = useState("");
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/organizations/current/settings");
      setSettings(r.data || {});
      setPicked(r.data?.timezone || "America/New_York");
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const options = useMemo(
    () => dedupe([settings?.timezone || "America/New_York", ...COMMON_ZONES]),
    [settings],
  );

  const isOwner = settings?.role === "owner";
  const activeTz = (custom.trim() || picked || "America/New_York");

  async function onSave() {
    setBusy(true); setError(null); setFlash(null);
    try {
      const r = await api.put("/organizations/current/settings/timezone", {
        timezone: activeTz,
      });
      setFlash(`Timezone updated to ${r.data?.timezone}${r.data?.timezone_abbr ? ` (${r.data?.timezone_abbr})` : ""}.`);
      await refreshOrgTimezone();
      await load();
      setCustom("");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell title="Settings">
      <div className="bo-page" data-testid="settings-page" style={{ padding: 24 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start",
                       marginBottom: 16, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div className="bo-eyebrow">Workspace</div>
            <h1 className="bo-serif" style={{ fontSize: 26, margin: "4px 0 4px" }}>
              Settings
            </h1>
            <p style={{ color: "var(--bo-muted)", maxWidth: 640, marginTop: 4 }}>
              Workspace-wide preferences. The timezone here drives how
              every publish time is rendered on this account.
            </p>
          </div>
          <Link to="/brand-os/publish/queue" className="bo-btn bo-btn-ghost"
                style={{ textDecoration: "none" }}>
            ← Queue
          </Link>
        </div>

        {error && (
          <div className="bo-card" data-testid="settings-error"
               style={{ padding: 12, color: "var(--bo-danger)", marginBottom: 12 }}>
            {typeof error === "string" ? error : error.message || JSON.stringify(error)}
          </div>
        )}
        {flash && (
          <div className="bo-card" data-testid="settings-flash"
               style={{ padding: 10, marginBottom: 12,
                        background: "var(--bo-accent-soft)",
                        color: "var(--bo-ink)", fontSize: 13 }}>
            {flash}
          </div>
        )}

        <div className="bo-card" style={{ padding: 18, display: "grid", gap: 14 }}
             data-testid="settings-timezone-card">
          <div>
            <div className="bo-eyebrow">Timezone</div>
            <h2 style={{ fontSize: 18, margin: "4px 0 0", fontWeight: 600 }}>
              Display and input timezone
            </h2>
            <p style={{ color: "var(--bo-muted)", fontSize: 13, marginTop: 4,
                        maxWidth: 640 }}>
              All publish surfaces (calendar, queue, receipts, fan-out,
              week scheduler, adapter explorer, and the daily digest)
              render times in this zone. Values typed into date-time
              inputs are interpreted in the same zone.
            </p>
          </div>

          <div data-testid="settings-current-tz" style={{
            padding: "10px 12px", borderRadius: 8,
            background: "var(--bo-panel-alt)", fontSize: 13,
          }}>
            Current: <strong>{tz.timezone}</strong>
            {tz.abbr ? ` · ${tz.abbr}` : ""}
          </div>

          <div style={{ display: "grid", gap: 8, maxWidth: 460 }}>
            <label className="bo-label">Pick a zone</label>
            <select
              className="bo-input"
              data-testid="settings-timezone-select"
              value={picked}
              onChange={(e) => setPicked(e.target.value)}
              disabled={!isOwner || busy}
            >
              {options.map((z) => (
                <option key={z} value={z}>{z}</option>
              ))}
            </select>
            <label className="bo-label" style={{ marginTop: 6 }}>
              Or type any valid IANA zone
            </label>
            <input
              className="bo-input"
              data-testid="settings-timezone-custom"
              placeholder="e.g. America/Puerto_Rico"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              disabled={!isOwner || busy}
            />
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              className="bo-btn bo-btn-primary"
              data-testid="settings-save-tz-btn"
              onClick={onSave}
              disabled={!isOwner || busy || !activeTz}
            >
              {busy ? "Saving…" : "Save timezone"}
            </button>
            {!isOwner && (
              <span style={{ fontSize: 12, color: "var(--bo-muted)" }}
                    data-testid="settings-owner-only-note">
                Only an owner can change workspace settings.
              </span>
            )}
          </div>
        </div>
      </div>
    </Shell>
  );
}
